import React from 'react';

import { ThreeDStockProductsCompactListItemButtonsProduct } from './ThreeDStockProductsCompactListItemButtons.types';
import {
  OnProductSimilarSearchAction,
  UpdateProductCacheAction
} from '../../../../../../products/productsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import useProductFavoriteButton from '../../../../../../products/components/buttons/ProductFavoriteButton/useProductFavoriteButton';

import { DownloadProductButton } from '../../../../../../products/components/buttons/DownloadProductButton';
import { ProductsCopyLinkButton } from '../../../../../../products/components/buttons/ProductsCopyLinkButton';
import { SubmitProductToProjectOrTaskModalButton } from '../../../../../../products/components/buttons/SubmitProductToProjectOrTaskModalButton';
import { ProductsListItemSimilarSearchButton } from '../../../../../../products/components/lists/ProductsListItem/components/ProductsListItemSimilarSearchButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { DropdownHelper } from '../../../../../../../helpers/dropdowns/DropdownHelper';
import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';

import { ProductPath } from '../../../../../../products/ProductPath';

import { ProductsPermissions } from '../../../../../../products/productsConstants';
import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';

import { aiSearchKeys, words } from '../../../../../../../locales/keys';
import { DownloadProductSeparatelyButton } from '../../../../../../products/components/buttons/DownloadProductSeparatelyButton';

interface ThreeDStockProductsCompactListItemButtonsProps {
  product: ThreeDStockProductsCompactListItemButtonsProduct;
  updateProductCache?: UpdateProductCacheAction<ThreeDStockProductsCompactListItemButtonsProduct>;
  onEditButtonMouseEnter?: () => void;
  onProductSimilarSearchFilter?: OnProductSimilarSearchAction;
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  findSimilarButtonAction: string;
  editButtonAction: string;
}

function ThreeDStockProductsCompactListItemButtons({
  product,
  updateProductCache,
  onEditButtonMouseEnter,
  onProductSimilarSearchFilter,
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  findSimilarButtonAction,
  editButtonAction
}: ThreeDStockProductsCompactListItemButtonsProps) {
  const { handleToggleFavorite, toggleProductFavoriteLoading } =
    useProductFavoriteButton({
      product,
      updateIndexProductCache: updateProductCache
    });

  return (
    <div className="flex justify-between gap-1 z-1 min-w-0">
      <div className="rounded-full flex min-w-0 bg-blue-500">
        <CheckPermissions
          action={ProductsPermissions.READ_PRODUCT_FAVORITE_BUTTON}
        >
          <ButtonHelper
            dataGa="products-product-favorite-button"
            className={
              product.favorite
                ? 'py-2.5 pl-5 pr-5 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out'
                : 'py-2.5 pl-5 pr-5 rounded-full inline-flex items-center leading-5 focus:ring-base text-white bg-blue-500 hover:bg-blue-600 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
            }
            onClick={handleToggleFavorite}
            disabled={toggleProductFavoriteLoading}
          >
            {product.favorite ? (
              <span>
                <Translate id={words.saved} />
              </span>
            ) : (
              <div className="text-sm whitespace-nowrap truncate space-x-1">
                <span>
                  <Translate id={words.save} />
                </span>
                <span className="hidden group-hover:inline group-focus-within:inline space-x-1">
                  <span className="text-blue-200 lowercase">
                    <Translate id={words.to} />
                  </span>
                  <span>
                    <Translate id={words.favorites} />
                  </span>
                </span>
              </div>
            )}
          </ButtonHelper>
        </CheckPermissions>
      </div>

      <div className="flex gap-1 xl:pr-2">
        <CheckPermissions action={downloadButtonAction}>
          <DownloadProductButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="products-product-download-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            productUuid={product.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <CheckPermissions action={downloadSeparatelyButtonAction}>
          <DownloadProductSeparatelyButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="products-product-download-separately-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            productUuid={product.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <DropdownHelper
          className="relative flex items-center justify-center"
          buttonClassName="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-gray-200 dark:text-white dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          icon={IconsEnum.DOTS_HORIZONTAL_OUTLINE}
          popoverClassName="flex flex-col items-end gap-1"
          dropdownPlacement={PopoverPlacement.TOP_END}
        >
          <CheckPermissions
            action={
              ProductsPermissions.READ_PRODUCT_SUBMIT_TO_TASK_OR_PROJECT_BUTTON
            }
          >
            <SubmitProductToProjectOrTaskModalButton
              dataGa="products-product-submit-to-task-or-project-button"
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              product={product}
              i18nText={words.attach}
              i18nTextClassName="inline"
              iconClassName="w-5 h-5"
            />
          </CheckPermissions>

          <CheckPermissions action={findSimilarButtonAction}>
            <ProductsListItemSimilarSearchButton
              dataGa="products-product-similar-search-filter-button"
              onProductSimilarSearch={onProductSimilarSearchFilter}
              product={product}
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              i18nText={aiSearchKeys.findSimilar}
              i18nTextClassName="inline"
              icon={IconsEnum.SEARCH_AI_SOLID}
            />
          </CheckPermissions>

          <CheckPermissions
            action={ProductsPermissions.READ_PRODUCT_COPY_LINK_BUTTON}
          >
            <ProductsCopyLinkButton
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              dataGa="products-product-copy-link-button"
              icon={IconsEnum.LINK_SOLID}
              iconClassName="h-5 w-5"
              product={product}
              i18nText={words.copyLink}
              i18nTextClassName="inline"
            />
          </CheckPermissions>

          <CheckPermissions action={editButtonAction}>
            <NextLinkHelper
              onMouseEnter={onEditButtonMouseEnter}
              dataGa="products-product-edit-link"
              href={ProductPath.edit(product.nanoId)}
              className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            >
              <Icon icon={IconsEnum.PENCIL} className="w-5 h-5" />
              <span>
                <Translate id={words.edit} />
              </span>
            </NextLinkHelper>
          </CheckPermissions>
        </DropdownHelper>
      </div>
    </div>
  );
}

export default ThreeDStockProductsCompactListItemButtons;
